import React, { useState, useRef } from "react";
import {
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    Button,
    TextField,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Alert,
    AlertColor,
    AlertPropsVariantOverrides,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import { Formik, Form, Field } from "formik";
import axiosInstance from "../../services/axios-instance";
import * as Yup from "yup";
import { OverridableStringUnion } from "@mui/types";
import axios from "axios";
import { Box } from "@mui/material";
import ExitToAppOutlinedIcon from "@mui/icons-material/ExitToAppOutlined";

const StyledTableCell = styled(TableCell)(({ theme }) => ({
    color: theme.palette.text.primary,
}));
const StyledTableRow = styled(TableRow)(() => ({
    backgroundColor: "white",
}));

interface Client {
    id: number;
    name: string;
}

const validationSchema = Yup.object({
    name: Yup.string().required("Client Name is required"),
});

const getTimelogs = (clientId: number) => {
    window.location.href = "timelog/client/" + clientId;
};

interface TableClientsProps {
    clients: Client[];
    setClients: React.Dispatch<React.SetStateAction<Client[]>>;
}

const TableClients = ({ clients, setClients }: TableClientsProps) => {
    const [editingClientId, setEditingClientId] = useState<number | null>(null);
    const [deleteClientId, setDeleteClientId] = useState<number | null>(null);
    const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
    const [alertMessage, setAlertMessage] = useState("");
    const [severity, setSeverity] = useState<AlertColor | undefined>("info");
    const [variant, setVariant] = useState<
        | OverridableStringUnion<
              "standard" | "filled" | "outlined",
              AlertPropsVariantOverrides
          >
        | undefined
    >("filled");

    // Create a ref for the form submission
    const formRef = useRef<any>(null);

    const handleEditClick = (clientId: number) => {
        setEditingClientId(clientId);
    };

    const handleSaveClick = async (values: Client) => {
        try {
            const response = await axiosInstance.patch(
                `/clients/${values.id}`,
                {
                    name: values.name,
                }
            );
            if (response.status === 200) {
                const updatedClients = clients.map((client) =>
                    client.id === values.id ? values : client
                );
                setClients(updatedClients);
                setSeverity("success");
                setAlertMessage("Client updated successfully");
                setEditingClientId(null);
            } else {
                setSeverity("error");
                setAlertMessage("Error updating client");
            }
        } catch (error) {
            if (axios.isAxiosError(error)) {
                setSeverity("error");
                setAlertMessage(error.response?.data.message);
            }
        }
    };

    const handleDeleteClick = (clientId: number) => {
        setDeleteClientId(clientId);
        setOpenDeleteDialog(true);
    };

    const handleDeleteConfirm = async () => {
        try {
            const response = await axiosInstance.delete(
                `/clients/${deleteClientId}`
            );

            if (response.status === 204 || response.status === 200) {
                const updatedClients = clients.filter(
                    (client) => client.id !== deleteClientId
                );
                setSeverity("success");
                setAlertMessage("Client deleted successfully");
                setClients(updatedClients);
            } else {
                setSeverity("error");
                setVariant("filled");
                setAlertMessage("Error deleting client");
            }
        } catch (error: any) {
            if (error.response) {
                if (axios.isAxiosError(error)) {
                    setSeverity("error");
                    setAlertMessage(error.response?.data.message);
                }
            }
        }
        setDeleteClientId(null);
        setOpenDeleteDialog(false);
    };

    const handleDeleteCancel = () => {
        setDeleteClientId(null);
        setOpenDeleteDialog(false);
    };

    return (
        <div>
            <TableContainer component={Paper}>
                <Box>
                    {alertMessage != "" && (
                        <Alert
                            onClick={() => setAlertMessage("")}
                            variant={variant}
                            severity={severity}
                        >
                            {alertMessage}
                        </Alert>
                    )}
                </Box>
                <Table>
                    <TableHead>
                        <TableRow
                            sx={{
                                backgroundColor: "#f9fafc",
                            }}
                        >
                            <StyledTableCell>ID</StyledTableCell>
                            <StyledTableCell>Name</StyledTableCell>
                            <StyledTableCell>Edit</StyledTableCell>
                            <StyledTableCell>Delete</StyledTableCell>
                            <StyledTableCell>Timelog</StyledTableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {clients.map((client) => (
                            <StyledTableRow key={client.id}>
                                <StyledTableCell>{client.id}</StyledTableCell>
                                <StyledTableCell>
                                    {editingClientId === client.id ? (
                                        <Formik
                                            initialValues={{ ...client }}
                                            validationSchema={validationSchema}
                                            onSubmit={handleSaveClick}
                                            innerRef={formRef}
                                        >
                                            {({
                                                handleSubmit,
                                                handleChange,
                                                errors,
                                                touched,
                                            }) => (
                                                <Form onSubmit={handleSubmit}>
                                                    <Field
                                                        as={TextField}
                                                        name="name"
                                                        error={
                                                            touched.name &&
                                                            !!errors.name
                                                        }
                                                        helperText={
                                                            touched.name &&
                                                            errors.name
                                                        }
                                                        onChange={handleChange}
                                                    />
                                                    <Button
                                                        style={{
                                                            display: "none",
                                                        }}
                                                        type="submit"
                                                        variant="contained"
                                                        color="primary"
                                                        onClick={() =>
                                                            formRef.current.submitForm()
                                                        } // Trigger form submission using ref
                                                    >
                                                        Save
                                                    </Button>
                                                </Form>
                                            )}
                                        </Formik>
                                    ) : (
                                        client.name
                                    )}
                                </StyledTableCell>
                                <StyledTableCell>
                                    {editingClientId === client.id ? (
                                        <Button
                                            variant="contained"
                                            color="secondary"
                                            onClick={() =>
                                                formRef.current.submitForm()
                                            }
                                        >
                                            Save
                                        </Button>
                                    ) : (
                                        <Button
                                            variant="contained"
                                            color="primary"
                                            onClick={() =>
                                                handleEditClick(client.id)
                                            }
                                        >
                                            Edit
                                        </Button>
                                    )}
                                </StyledTableCell>
                                <StyledTableCell>
                                    <Button
                                        variant="contained"
                                        color="error"
                                        onClick={() =>
                                            handleDeleteClick(client.id)
                                        }
                                    >
                                        Delete
                                    </Button>
                                </StyledTableCell>
                                <StyledTableCell>
                                    <Button
                                        onClick={() =>
                                            getTimelogs(client.id)
                                            }
                                            >
                                        <ExitToAppOutlinedIcon />
                                    </Button>
                                </StyledTableCell>
                            </StyledTableRow>
                        ))}
                    </TableBody>
                </Table>
            </TableContainer>
            <Dialog open={openDeleteDialog} onClose={handleDeleteCancel}>
                <DialogTitle>Confirm Delete</DialogTitle>
                <DialogContent>
                    Are you sure you want to delete this client?
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleDeleteCancel} color="primary">
                        Cancel
                    </Button>
                    <Button onClick={handleDeleteConfirm} color="secondary">
                        Delete
                    </Button>
                </DialogActions>
            </Dialog>
        </div>
    );
};

export default TableClients;
